import { getLargestZone } from "@omnia/shared/layers/utils"
import { SoilAnalysisLayer } from "../../../map/soilAnalysisLayer"
import { Datalayer, FieldViewModel, groupBy, IGenericAnalysis, IGenericAnalysisAlloc, MapGeometryType, models, SoilMapView } from "../../../omnia"
import { IDataLayer, IDataLayerType, IDataLayerZone, ILayerViewModel, ISoilAnalysisLayerViewerFactory, LayerTypeId } from "../../../types"
import { getCurrentAnalysisLayer } from "../../utils"
import { groupMapFeaturesInSingleLayer } from "../context"
import { IViewerContext } from "../types"

export abstract class AnalysisView<TAnalysis extends IGenericAnalysis> extends SoilMapView {

    protected _layerType: IDataLayerType;

    protected abstract getAnalysisId(a: TAnalysis): number
    protected abstract checkZoneAllocation(alloc: IGenericAnalysisAlloc): boolean;

    constructor(
        protected readonly ctxt: IViewerContext,
        protected readonly analysis: TAnalysis[],
        layerTypeId: LayerTypeId,
        protected readonly layers: IDataLayer[],
        protected readonly layerViewer: ISoilAnalysisLayerViewerFactory,
        protected readonly yearSelector: (a: TAnalysis) => number
    ) {

        super(layerViewer.name, layerViewer.key);

        this._layerType = ctxt.soilSvc.getLayerType(layerTypeId);

        this.setupColors(layerViewer.colors);

        const currentYear = ctxt.soilSvc.yearId

        let vm = this.createStdLayers().concat(this.createVirtualLayers(analysis, layers))

        vm = vm.filter(l => {
            const year = l.datalayer.FirstYearId || 0
            return (!year) || (year <= currentYear)
        })

        vm = groupBy(vm, a => a.fieldId)
            .map(g => {
                const layers = g.values.sort((a, b) => (b.datalayer.FirstYearId || 0) - (a.datalayer.FirstYearId || 0))
                return layers[0]
            })

        vm.forEach(i => {
            i.showTooltips = false;
            i.selectable = true;
        });

        this._layers = vm;

    }

    private createVirtualLayers(analysis: TAnalysis[], layers: IDataLayer[]) {

        // 211105 - Usersnap 2328: do not create virtual layers for inactive fields
        let fields = this.ctxt.soilSvc.activeFields
        fields = fields.filter(f => f.hasBoundary())


        return fields.map(f => {

            let fieldAnalysis = analysis
                .filter(a => {

                    const allocations = (a.Allocations ?? []).filter(aa => aa.FieldId === f.fieldId)

                    if (allocations.some(aa => !this.checkZoneAllocation(aa))) {
                        return true
                    }


                })
                .sort((a1, a2) => a2.SamplingDate.valueOf() - a1.SamplingDate.valueOf())
                .find(a => true)

            if (fieldAnalysis) {

                let datalayer = this.createVirtualLayer(f, fieldAnalysis);

                let options = {
                    addToMap: !groupMapFeaturesInSingleLayer,
                };

                let vm = this.ctxt.createLayerViewModel(datalayer, f, options);

                return vm;

            }

        }).filter(l => !!l);

    }

    private createVirtualLayer(field: FieldViewModel, analysis: TAnalysis) {

        let zones: IDataLayerZone[] = [

            {
                DataLayerZoneId: 0,
                Number: 0,
                Geometry: field.field.Boundary,
                Value: {
                    Data: analysis,
                    Name: analysis.AnalysisNumber,
                    Value: this.getAnalysisId(analysis)
                },
                Data: analysis,
                Name: field.fieldName
            }

        ];

        let layer: IDataLayer = {
            DataLayerId: 0,
            FirstYearId: this.yearSelector(analysis),
            GeometryType: MapGeometryType.Polygon,
            TypeId: this._layerType.TypeId,
            Type: this._layerType,
            FieldId: field.fieldId,
            Zones: zones
        }

        return layer;

    }

    private createStdLayers() {

        const ctxt = this.ctxt.datalayerContext
        const year = this.ctxt.soilSvc.yearId

        const layers = groupBy(this.layers, l => l.FieldId)
            .map(g => getCurrentAnalysisLayer({ fieldId: g.key }, year, g.values))
            .filter(l => !!l)

        const vm = layers
            .map(l => {

                let field = this.ctxt.soilSvc.findField(l.FieldId, false)

                if (field) {

                    let instance = new SoilAnalysisLayer(l, ctxt);

                    this.setStdLayerZoneName(field, instance);

                    let options = {
                        layerInstance: instance,
                        addToMap: !groupMapFeaturesInSingleLayer,
                    };

                    return this.ctxt.createLayerViewModel(l, field, options)

                }

            })
            .filter(l => !!l)

        return vm

    }

    private setStdLayerZoneName(field: FieldViewModel, instance: Datalayer) {

        let largest = getLargestZone(instance)

        if (largest) {
            largest.zoneName = field.fieldName + "\n" + largest.getDefaultZoneName();
        }

    }


    public getLayers() {
        return this._layers;
    }

    protected showLayer(l: ILayerViewModel, visibilityFilter?: (l: ILayerViewModel) => boolean) {
        const visible = visibilityFilter ? visibilityFilter(l) : true
        l.setViewer(this.layerViewer)
        l.selectable = visible
        l.visible = visible
    }

}
